"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { motion, useAnimation } from "framer-motion";
import { Button } from "../../../ui/button";

import { CHART_COLORS } from "../../../../constants/colors";

// Add debounce utility
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Icon Components
const KubernetesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    x="0px"
    y="0px"
    width="100"
    height="100"
    viewBox="0,0,256,256"
  >
    <g
      fill={CHART_COLORS.PRIMARY_GREEN}
      fillRule="nonzero"
      stroke="none"
      strokeWidth="1"
      strokeLinecap="butt"
      strokeLinejoin="miter"
      strokeMiterlimit="10"
      strokeDasharray=""
      strokeDashoffset="0"
      fontFamily="none"
      fontWeight="none"
      fontSize="none"
      textAnchor="none"
      style={{ mixBlendMode: "normal" }}
    >
      <g transform="scale(5.12,5.12)">
        <path d="M25,3.27539c-0.3725,0 -0.74484,0.08391 -1.08984,0.25391l-15.46094,7.46094c-0.69,0.34 -1.18938,0.95899 -1.35938,1.70899l-3.82031,16.78125c-0.17,0.75 0.01047,1.52914 0.48047,2.11914l10.69922,13.46094c0.48,0.6 1.2107,0.94922 1.9707,0.94922h17.16016c0.76,0 1.4907,-0.34922 1.9707,-0.94922l10.69922,-13.46094c0.47,-0.59 0.65047,-1.36914 0.48047,-2.11914l-3.82031,-16.78125c-0.17,-0.75 -0.66938,-1.36898 -1.35937,-1.70899l-15.46094,-7.46094c-0.345,-0.17 -0.71734,-0.25391 -1.08984,-0.25391zM24.99219,8.48438c0.42328,-0.00094 0.82781,0.24375 1.00781,0.65625c0.12,0.29 0.11,0.59938 0,0.85938c-0.23,0.54 -0.30914,1.07914 -0.36914,1.61914c-0.02,0.28 -0.03148,0.55031 -0.02148,0.82031c-0.03,0.27 0.11055,0.54055 0.31055,0.81055c0.21,0.27 0.22977,0.54055 0.25977,0.81055c2.82,0.27 5.35977,1.51992 7.25977,3.41992c0.23,-0.15 0.45078,-0.31031 0.80078,-0.32031c0.33,-0.02 0.63031,-0.0793 0.82031,-0.2793c0.21,-0.17 0.40937,-0.35102 0.60938,-0.54102c0.38,-0.38 0.74953,-0.79055 1.01953,-1.31055c0.27,-0.54 0.93094,-0.74875 1.46094,-0.46875c0.54,0.27 0.74875,0.92898 0.46875,1.45898c-0.14,0.28 -0.3782,0.47078 -0.6582,0.55078c-0.56,0.16 -1.03047,0.45 -1.48047,0.75c-0.22,0.16 -0.44062,0.32 -0.64062,0.5c-0.23,0.15 -0.34945,0.43 -0.43945,0.75c-0.07,0.34 -0.26117,0.53047 -0.45117,0.73047c1.3,1.91 2.05078,4.21922 2.05078,6.69922c0,0.38 -0.02078,0.76086 -0.05078,1.13086h0.01172c0.26,0.08 0.52,0.14992 0.75,0.41992c0.23,0.24 0.46828,0.41945 0.73828,0.43945c0.27,0.06 0.53078,0.09891 0.80078,0.12891c0.55,0.05 1.08992,0.07141 1.66992,-0.05859c0.59,-0.13 1.17078,0.24008 1.30078,0.83008c0.13,0.58 -0.24008,1.15906 -0.83008,1.28906c-0.3,0.07 -0.59984,-0.00039 -0.83984,-0.15039c-0.49,-0.33 -1.01125,-0.51016 -1.53125,-0.66016c-0.26,-0.07 -0.52906,-0.12992 -0.78906,-0.16992c-0.26,-0.08 -0.56133,0.00062 -0.86133,0.14063c-0.3,0.17 -0.56961,0.14109 -0.84961,0.12109h-0.0293c-0.86,2.85 -2.75,5.25906 -5.25,6.78906v0.00977c0.08,0.26 0.16984,0.50961 0.08984,0.84961c-0.07,0.33 -0.07945,0.63109 0.06055,0.87109c0.11,0.24 0.22938,0.48875 0.35938,0.71875c0.27,0.47 0.56977,0.94008 1.00977,1.33008c0.44,0.41 0.47031,1.09101 0.07031,1.54102c-0.41,0.44 -1.09101,0.47031 -1.54102,0.07031c-0.23,-0.21 -0.34961,-0.49125 -0.34961,-0.78125c-0.02,-0.59 -0.16961,-1.10891 -0.34961,-1.62891c-0.09,-0.25 -0.19055,-0.5 -0.31055,-0.75c-0.09,-0.26 -0.32914,-0.44133 -0.61914,-0.61133c-0.28,-0.15 -0.42055,-0.35984 -0.56055,-0.58984c-1.28,0.47 -2.65961,0.7207 -4.09961,0.7207c-1.42,0 -2.77906,-0.24922 -4.03906,-0.69922c-0.12,0.22 -0.26102,0.41836 -0.54102,0.56836c-0.29,0.16 -0.52914,0.35133 -0.61914,0.61133c-0.12,0.24 -0.23031,0.49023 -0.32031,0.74023c-0.18,0.52 -0.33961,1.03891 -0.34961,1.62891c-0.02,0.6 -0.53086,1.07078 -1.13086,1.05078c-0.6,-0.02 -1.07078,-0.52109 -1.05078,-1.12109c0.01,-0.31 0.15133,-0.57953 0.36133,-0.76953c0.44,-0.4 0.73953,-0.86031 1.01953,-1.32031c0.13,-0.24 0.24938,-0.47852 0.35938,-0.72852c0.14,-0.23 0.13031,-0.54109 0.07031,-0.87109c-0.07,-0.31 0.00008,-0.55906 0.08008,-0.78906c-2.51,-1.51 -4.43055,-3.91172 -5.31055,-6.76172c-0.23,0.02 -0.44899,0.02062 -0.70899,-0.10937c-0.31,-0.13 -0.59937,-0.20914 -0.85937,-0.11914c-0.27,0.05 -0.53102,0.11922 -0.79102,0.19922c-0.52,0.17 -1.02977,0.36117 -1.50977,0.70117c-0.49,0.35 -1.16953,0.22828 -1.51953,-0.26172c-0.34,-0.49 -0.23023,-1.16977 0.25977,-1.50977c0.25,-0.18 0.55984,-0.23945 0.83984,-0.18945c0.57,0.12 1.12016,0.07977 1.66016,0.00977c0.27,-0.03 0.53883,-0.08039 0.79883,-0.15039c0.27,-0.02 0.51047,-0.21875 0.73047,-0.46875c0.19,-0.22 0.41086,-0.31063 0.63086,-0.39062c-0.04,-0.41 -0.06055,-0.83 -0.06055,-1.25c0,-2.56 0.79992,-4.93086 2.16992,-6.88086c-0.18,-0.18 -0.34992,-0.35969 -0.41992,-0.67969c-0.09,-0.32 -0.20945,-0.6 -0.43945,-0.75c-0.21,-0.18 -0.42062,-0.34023 -0.64062,-0.49023c-0.46,-0.3 -0.93023,-0.58 -1.49023,-0.75c-0.58,-0.16 -0.92,-0.76984 -0.75,-1.33984c0.17,-0.58 0.76961,-0.91 1.34961,-0.75c0.29,0.09 0.53016,0.29102 0.66016,0.54102c0.27,0.52 0.64125,0.93055 1.03125,1.31055c0.19,0.19 0.39937,0.3693 0.60938,0.5293c0.19,0.2 0.49031,0.2593 0.82031,0.2793c0.35,0.01 0.56883,0.16055 0.79883,0.31055l0.01172,0.00977c1.88,-1.82 4.35937,-3.0193 7.10937,-3.2793c0.03,-0.27 0.04977,-0.54055 0.25977,-0.81055c0.2,-0.27 0.34055,-0.54055 0.31055,-0.81055c0.01,-0.27 -0.00148,-0.54031 -0.02148,-0.82031c-0.06,-0.54 -0.13914,-1.07914 -0.36914,-1.61914c-0.24,-0.55 0.02031,-1.18969 0.57031,-1.42969c0.1375,-0.0575 0.28078,-0.08563 0.42188,-0.08594zM23.56055,16.10938c-1.93,0.27 -3.68008,1.10031 -5.08008,2.32031l0.30859,0.18945c0.93,0.58 1.83047,1.19078 2.73047,1.80078c0.52,0.33 1.01,0.70078 1.5,1.05078c0.04,-0.57 0.09039,-1.1507 0.15039,-1.7207c0.1,-1.08 0.19984,-2.17 0.33984,-3.25zM26.43945,16.10938l0.05078,0.39063c0.14,1.08 0.23984,2.17 0.33984,3.25c0.04,0.43 0.07937,0.8493 0.10938,1.2793c0.005,0.0825 0.0325,0.15547 0.07422,0.21484c0.04172,0.05937 0.09773,0.10672 0.16211,0.13672c0.12875,0.06 0.28883,0.05398 0.42383,-0.04102c0.34,-0.25 0.69078,-0.50023 1.05078,-0.74023c0.9,-0.61 1.78898,-1.22055 2.70898,-1.81055l0.33984,-0.20898c-0.17875,-0.1625 -0.36219,-0.31945 -0.55273,-0.46875c-0.19055,-0.1493 -0.38828,-0.29047 -0.58984,-0.42578c-0.20156,-0.13531 -0.40734,-0.26422 -0.61914,-0.38477c-0.2118,-0.12055 -0.42914,-0.23289 -0.65039,-0.33789c-0.22125,-0.105 -0.44586,-0.20234 -0.67578,-0.29102c-0.22992,-0.08867 -0.46531,-0.16867 -0.70312,-0.24023c-0.23781,-0.07156 -0.47969,-0.13383 -0.72461,-0.1875c-0.24492,-0.05367 -0.49289,-0.09977 -0.74414,-0.13477zM16.58008,20.60938c-0.25,0.3875 -0.47297,0.7943 -0.66797,1.2168c-0.0975,0.21125 -0.18867,0.42742 -0.27148,0.64648c-0.08281,0.21906 -0.15906,0.44172 -0.22656,0.66797c-0.0675,0.22625 -0.12617,0.45469 -0.17773,0.6875c-0.05156,0.23281 -0.09586,0.47023 -0.13086,0.70898c-0.035,0.23875 -0.06031,0.47859 -0.07812,0.72266c-0.01781,0.24406 -0.02734,0.49148 -0.02734,0.74023c0,0.19 0.00977,0.37078 0.00977,0.55078l0.4707,-0.16016c1.03,-0.37 2.06961,-0.69125 3.09961,-1.03125c0.49,-0.16 0.9807,-0.29922 1.4707,-0.44922c0.155,-0.045 0.25391,-0.16453 0.28516,-0.30078c0.01562,-0.06812 0.01461,-0.14008 -0.00586,-0.20898c-0.02047,-0.06891 -0.05914,-0.13445 -0.11914,-0.18945c-0.34,-0.31 -0.67,-0.62117 -1,-0.95117c-0.78,-0.77 -1.55055,-1.53031 -2.31055,-2.32031zM33.53906,20.80078l-0.26953,0.28906c-0.75,0.8 -1.51906,1.56984 -2.28906,2.33984c-0.49,0.5 -1.02125,0.9807 -1.53125,1.4707c0.68,0.17 1.36102,0.34883 2.04102,0.54883c1.05,0.3 2.08891,0.59164 3.12891,0.93164l0.37109,0.11914c0.01,-0.16 0.00977,-0.33 0.00977,-0.5c0,-0.4775 -0.03344,-0.94719 -0.09766,-1.40625c-0.06422,-0.45906 -0.15883,-0.9075 -0.2832,-1.34375c-0.12438,-0.43625 -0.27852,-0.86047 -0.45898,-1.26953c-0.18047,-0.40906 -0.38859,-0.80219 -0.62109,-1.17969zM24.37695,24.07422c-0.189,0 -0.3648,0.08837 -0.4668,0.23438l-0.78711,0.97461c-0.101,0.131 -0.14423,0.32109 -0.11523,0.49609l0.27734,1.20898c0.029,0.189 0.16031,0.33488 0.32031,0.42188l1.12109,0.52344c0.16,0.087 0.36539,0.087 0.52539,0l1.12109,-0.52344c0.16,-0.087 0.29131,-0.23287 0.32031,-0.42187l0.27734,-1.20898c0.03,-0.175 -0.01519,-0.36509 -0.11719,-0.49609l-0.78711,-0.97461c-0.102,-0.146 -0.2778,-0.23437 -0.4668,-0.23437zM20.76953,28.76953c-0.47,0.06 -0.93039,0.12969 -1.40039,0.17969c-1.09,0.13 -2.16,0.26133 -3.25,0.36133l-0.53906,0.04883c0.09125,0.255 0.19168,0.50684 0.30273,0.75195c0.11106,0.24512 0.23344,0.48453 0.36328,0.71875c0.12984,0.23422 0.2684,0.46324 0.41602,0.68555c0.29523,0.44461 0.62414,0.86305 0.98438,1.25391c0.18012,0.19543 0.36766,0.38399 0.5625,0.56445c0.19484,0.18047 0.39691,0.35309 0.60547,0.51758c0.20856,0.16449 0.42523,0.32125 0.64648,0.46875l0.13867,-0.41992c0.34,-1.04 0.70031,-2.05984 1.07031,-3.08984c0.17,-0.5 0.35102,-0.99023 0.54102,-1.49023c0.03,-0.0725 0.0368,-0.14578 0.02539,-0.21484c-0.01141,-0.06906 -0.04281,-0.13375 -0.08594,-0.1875c-0.04312,-0.05375 -0.09891,-0.09648 -0.16406,-0.12305c-0.06516,-0.02656 -0.1393,-0.03789 -0.2168,-0.02539zM29.41016,28.91016c-0.075,-0.01 -0.14602,0.00031 -0.20898,0.02734c-0.06297,0.02703 -0.11828,0.06992 -0.16016,0.12305c-0.04187,0.05313 -0.07031,0.11727 -0.08203,0.18555c-0.01172,0.06828 -0.00602,0.14234 0.02148,0.21484c0.17,0.45 0.33023,0.89937 0.49023,1.35937c0.35,1.02 0.70906,2.04984 1.03906,3.08984l0.10938,0.35938c0.2175,-0.14875 0.42994,-0.30584 0.63477,-0.4707c0.20482,-0.16486 0.40258,-0.3375 0.59375,-0.51758c0.38234,-0.36016 0.73672,-0.74844 1.05859,-1.16406c0.16094,-0.20781 0.31463,-0.42225 0.45898,-0.64258c0.14436,-0.22033 0.27945,-0.44773 0.40625,-0.67969c0.1268,-0.23195 0.24525,-0.47021 0.35352,-0.71289c0.10826,-0.24268 0.20617,-0.48969 0.29492,-0.74219l-0.34961,-0.01953c-1.09,-0.07 -2.16977,-0.16953 -3.25977,-0.26953c-0.47,-0.04 -0.93039,-0.09062 -1.40039,-0.14062zM25.07422,30.88281c-0.07125,0 -0.14164,0.01844 -0.20508,0.05469c-0.06344,0.03625 -0.1182,0.08961 -0.1582,0.16211c-0.23,0.43 -0.4707,0.84977 -0.7207,1.25977c-0.55,0.94 -1.09969,1.88078 -1.67969,2.80078l-0.25,0.40039c0.2325,0.0725 0.4675,0.13656 0.70703,0.19141c0.47906,0.10969 0.97109,0.18352 1.47266,0.2207c0.25078,0.01859 0.50477,0.02734 0.75977,0.02734c0.2625,0 0.52313,-0.00945 0.78125,-0.0293c0.25812,-0.01984 0.51312,-0.05047 0.76563,-0.08984c0.2525,-0.03937 0.50242,-0.08789 0.74805,-0.14648c0.24563,-0.05859 0.48711,-0.12758 0.72461,-0.20508l-0.23047,-0.37891c-0.58,-0.92 -1.1182,-1.87055 -1.6582,-2.81055c-0.24,-0.41 -0.47141,-0.82024 -0.69141,-1.24023c-0.08,-0.145 -0.22273,-0.2168 -0.36523,-0.2168z"></path>
      </g>
    </g>
  </svg>
);

const CloudSQLIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    className="flex-shrink-0"
    fill={CHART_COLORS.PRIMARY_GREEN}
  >
    <path d="M12 3C7.58 3 4 4.79 4 7V17C4 19.21 7.59 21 12 21S20 19.21 20 17V7C20 4.79 16.42 3 12 3M18 17C18 17.5 15.87 19 12 19S6 17.5 6 17V14.77C7.61 15.55 9.72 16 12 16S16.39 15.55 18 14.77V17M18 12.45C16.7 13.4 14.42 14 12 14C9.58 14 7.3 13.4 6 12.45V9.64C7.47 10.47 9.61 11 12 11C14.39 11 16.53 10.47 18 9.64V12.45M12 9C8.13 9 6 7.5 6 7S8.13 5 12 5C15.87 5 18 6.5 18 7S15.87 9 12 9Z" />
  </svg>
);

const StorageIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L3 7L12 12L21 7L12 2Z"
      stroke={CHART_COLORS.PRIMARY_GREEN}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 17L12 22L21 17"
      stroke={CHART_COLORS.PRIMARY_GREEN}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 12L12 17L21 12"
      stroke={CHART_COLORS.PRIMARY_GREEN}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VPCIcon = () => (
  <svg
    width="24"
    height="20"
    viewBox="0 0 24 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8465 8.61459C14.4051 8.61459 15.67 9.88094 15.67 11.4381V12.8499H17.0818V17.791C17.0818 18.9586 16.1331 19.9087 14.9641 19.9087H10.7289C9.56131 19.9087 8.6112 18.9586 8.6112 17.791V12.8499H10.023V11.4381C10.023 9.88094 11.2893 8.61459 12.8465 8.61459ZM15.67 14.2616H10.023V17.791C10.023 18.1807 10.3406 18.4969 10.7288 18.4969H14.9641C15.3538 18.4969 15.67 18.1807 15.67 17.791V14.2617L15.67 14.2616ZM12 0.0875244C14.7727 0.0875244 17.1106 1.88329 17.976 4.36517C18.1031 4.3567 18.2259 4.32282 18.3529 4.32282C21.4659 4.32282 24 6.85552 24 9.96987C24 13.0842 21.4659 15.6169 18.3529 15.6169V14.2052C20.688 14.2052 22.5882 12.3049 22.5882 9.96987C22.5882 7.63482 20.688 5.73459 18.3529 5.73459C18.3289 5.73459 18.3064 5.74164 18.2824 5.74305C18.3092 5.97459 18.3529 6.20187 18.3529 6.44046C18.3529 6.99529 18.2809 7.54446 18.1412 8.07529L16.776 7.71105C16.8861 7.30022 16.9412 6.87246 16.9412 6.44046C16.9412 3.71576 14.7247 1.49929 12 1.49929C9.34447 1.49929 7.18587 3.61129 7.07859 6.24C7.55718 6.47152 8.00894 6.76375 8.39859 7.14494L7.41176 8.15435C6.74682 7.50352 5.87011 7.14635 4.94117 7.14635C2.99576 7.14635 1.41176 8.73035 1.41176 10.6758C1.41176 12.6212 2.99576 14.2052 4.94117 14.2052H7.05883V15.6169H4.94117C2.21648 15.6169 0 13.4005 0 10.6758C0 7.95106 2.21648 5.7346 4.94117 5.7346C5.20235 5.7346 5.45789 5.76847 5.71059 5.80942C6.03246 2.60611 8.71341 0.0875244 12 0.0875244ZM12.8465 10.0264C12.0686 10.0264 11.4347 10.6602 11.4347 11.4381V12.8499H14.2583V11.4381C14.2583 10.6602 13.6258 10.0264 12.8465 10.0264Z"
      fill={CHART_COLORS.PRIMARY_GREEN}
    />
  </svg>
);

// Default cloud icon as a component
const CloudIcon = () => (
  <img
    src="/cloud-icon.svg"
    alt="cloud"
    className="w-4 h-4 flex-shrink-0"
    loading="lazy"
  />
);

const VMIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    className="flex-shrink-0"
    fill={CHART_COLORS.PRIMARY_GREEN}
  >
    <path d="M13 19H14C14.2652 19 14.5196 19.1054 14.7071 19.2929C14.8946 19.4804 15 19.7348 15 20V21H9V20C9 19.7348 9.10536 19.4804 9.29289 19.2929C9.48043 19.1054 9.73478 19 10 19H11V17H13V19ZM4 5H20C20.2652 5 20.5196 5.10536 20.7071 5.29289C20.8946 5.48043 21 5.73478 21 6V16C21 16.2652 20.8946 16.5196 20.7071 16.7071C20.5196 16.8946 20.2652 17 20 17H4C3.73478 17 3.48043 16.8946 3.29289 16.7071C3.10536 16.5196 3 16.2652 3 16V6C3 5.73478 3.10536 5.48043 3.29289 5.29289C3.48043 5.10536 3.73478 5 4 5ZM5 7V15H19V7H5Z" />
  </svg>
);

// Icon mapping configuration
const SUGGESTION_ICONS: Record<string, React.FC> = {
  kubernetes: KubernetesIcon,
  "cloud-sql": CloudSQLIcon,
  storage: StorageIcon,
  vpc: VPCIcon,
  vm: VMIcon,
};

// Utility function to get the appropriate icon
const getSuggestionIcon = (
  iconName: string,
  _title?: string
): React.ReactNode => {
  const IconComponent = iconName ? SUGGESTION_ICONS[iconName] : CloudIcon;
  return <IconComponent />;
};

interface MovingButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  iconName?: string;
}

const MovingButton: React.FC<MovingButtonProps> = React.memo(
  ({ children, onClick, title, iconName }) => {
    // Debounce the click handler
    const debouncedClick = useMemo(
      () =>
        debounce((e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }, 300),
      [onClick]
    );

    return (
      <Button
        onClick={debouncedClick}
        variant="outline"
        size="sm"
        className="rounded-full text-base font-light flex items-center bg-white border-[#E1E1E1] text-[#808080] px-[25px] py-[25px] hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md whitespace-nowrap mx-2"
        title={title}
      >
        <div className="flex items-center gap-2">
          {getSuggestionIcon(iconName || "", title)}
          {children}
        </div>
      </Button>
    );
  }
);

interface SuggestionCarouselProps {
  suggestions: readonly {
    readonly title: string;
    readonly description: string;
    readonly icon: string;
  }[];
  onSuggestionClick: (suggestion: string) => void;
  isNavigating: boolean;
}

const SuggestionCarousel: React.FC<SuggestionCarouselProps> = React.memo(
  ({ suggestions, onSuggestionClick, isNavigating }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const controls = useAnimation();
    const [isPaused, setIsPaused] = useState(false);
    const isAnimating = useRef(false);

    // Measure container width only once after mount or on resize
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setWidth(containerRef.current.scrollWidth / 2);
        }
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      return () => resizeObserver.disconnect();
    }, []);

    // Optimized animation function
    const startAnimation = useCallback(async () => {
      if (width === 0 || isPaused || isAnimating.current) return;

      isAnimating.current = true;
      try {
        // Reset position to 0 without animation first
        await controls.set({ x: 0 });

        // Start the infinite animation
        await controls.start({
          x: -width,
          transition: {
            duration: 30, // Slower animation for smoother effect
            ease: "linear",
            repeat: Infinity,
            repeatType: "loop",
          },
        });
      } catch (error) {
        console.error("Animation error:", error);
      }
      isAnimating.current = false;
    }, [controls, width, isPaused]);

    // Handle animation start and cleanup
    useEffect(() => {
      if (!isPaused && width > 0) {
        startAnimation();
      }
      return () => {
        controls.stop();
        isAnimating.current = false;
      };
    }, [width, isPaused, startAnimation, controls]);

    const handleMouseEnter = useCallback(() => {
      setIsPaused(true);
      controls.stop();
    }, [controls]);

    const handleMouseLeave = useCallback(() => {
      setIsPaused(false);
    }, []);

    // Resume animation when mouse leaves
    useEffect(() => {
      if (!isPaused && width > 0) {
        const timeoutId = setTimeout(() => {
          startAnimation();
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }, [isPaused, width, startAnimation]);

    const handleButtonClick = useCallback(
      (description: string) => {
        if (isNavigating) return;

        // Populate the textarea with the suggestion instantly
        onSuggestionClick(description);
      },
      [isNavigating, onSuggestionClick]
    );

    // Memoize suggestion items
    const suggestionItems = useMemo(() => {
      // Create two identical sets of items for smooth infinite scroll
      const singleSet = suggestions.map((suggestion, i) => (
        <MovingButton
          key={`set1-${suggestion.title}-${i}`}
          onClick={() => handleButtonClick(suggestion.description)}
          title={suggestion.description}
          iconName={suggestion.icon}
        >
          {suggestion.title}
        </MovingButton>
      ));

      const duplicateSet = suggestions.map((suggestion, i) => (
        <MovingButton
          key={`set2-${suggestion.title}-${i}`}
          onClick={() => handleButtonClick(suggestion.description)}
          title={suggestion.description}
          iconName={suggestion.icon}
        >
          {suggestion.title}
        </MovingButton>
      ));

      return [...singleSet, ...duplicateSet];
    }, [suggestions, handleButtonClick]);

    return (
      <div className="w-full max-w-[741px] overflow-hidden pb-4">
        <motion.div
          ref={containerRef}
          animate={controls}
          className="inline-flex items-center py-4"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          initial={{ x: 0 }}
          style={{
            touchAction: "none",
            willChange: "transform",
          }}
        >
          {suggestionItems}
        </motion.div>
      </div>
    );
  }
);

export default SuggestionCarousel;
